import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Grid, Input, TextField, Typography } from "@mui/material";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import React from "react";
import { toast } from "react-toastify";
import { CoreExportService } from "../../../Component/Api/Export/CoreExportService";
import { ImportService } from "../../../Component/Api/Import/ImportService";
import { MDAlert } from "../../../Component/Common/Components/MDAlert";
import { MDBox } from "../../../Component/Common/Components/MDBox";
import { MDButton } from "../../../Component/Common/Components/MDButton";
import { MDTypography } from "../../../Component/Common/Components/MDTypography";
import { Export } from "../../../Component/Model/Dto/Export";
import { ExportKeyValue, FileExportRequest } from "../../../Component/Model/Dto/Upload";
import { DataImportType } from "../../../Component/Model/Enum/BEE123Enums";
import { CustomDialogTitle } from "../Custom/CustomDialogTitle";
import { ImportPopup } from "./ImportPopup";
import { getDateWithoutTime } from "../../../Helpers/DateFormatter";

interface IProps {
    parameters: ExportKeyValue[];
    type: string;
    typeId: DataImportType;
    scorecardId?: string;
    uploadType: string;
    controlsEnabled: boolean;
    showHelpfileDownload?: boolean;
    importBus: boolean;
    coreExportEnabled: boolean;
    coreImportEnabled: boolean;
    showYearToAdd?: boolean;
    reloadTable: () => void;
    showAdditionalParamsModal?: boolean;
    disableExport?: boolean;
    disableImport?: boolean;
}

export const ImportExportButtonsDialog = (props: IProps) => {

    const [openImportPopup, setOpenImportPopup] = React.useState<boolean>(false);
    const [yearToExport, setYearToExport] = React.useState(new Date());
    const [isLoading, setIsLoading] = React.useState(false);
    const [isImport, setIsImport] = React.useState(true);

    const coreFileExport = async () => {
        let params = props.parameters.slice();

        if (props.showAdditionalParamsModal) {
            params.push({ key: "YearToExport", value: yearToExport.getFullYear().toString() });
        }

        const request: Export.FileExportDto = {
            DataImportType: props.typeId,
            parameters: params,
            scorecardId: props.scorecardId
        };
        setIsLoading(true);
        try {
            let response = await CoreExportService.ExportFile(request);
            const redirectUrl = response.data;
            window.open(redirectUrl, '_blank');
        } catch (error) {
            console.log(error);
            toast.error("Export could not be completed");
        } finally {
            setIsLoading(false);
            params = [];
        }
    }

    const onExportData = async () => {
        if (props.coreExportEnabled) {
            if (props.showAdditionalParamsModal) {
                setIsImport(false);
                setOpenImportPopup(true);
            } else {
                await coreFileExport();
            }
        } else {
            const parameters: FileExportRequest = {
                Parameters: props.parameters,
                Type: props.type
            }
            setIsLoading(true);
            try {
                const file = await ImportService.ExportFile(parameters);
                var csvContent = "data:text/csv;charset=utf-8," + file.data;
                var encodedUri = encodeURI(csvContent);
                var fixedEncodedURI = encodedUri.replaceAll('#', '%23');
                var link = document.createElement("a");
                link.setAttribute("href", fixedEncodedURI);
                link.setAttribute(
                    "download", props.uploadType + ".csv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                console.log(error)
            } finally {
                setIsLoading(false);
            }
        }
    }

    const onCloseClick = () => {
        setOpenImportPopup(false)
        props.reloadTable();
    }

    return (
        <>
            <Dialog open={openImportPopup} onClose={() => setOpenImportPopup(false)} aria-labelledby="form-dialog-title" maxWidth='lg' fullWidth>
                <CustomDialogTitle id="customized-dialog-title" onClose={onCloseClick}>
                    File {isImport ? "Import" : "Export"}
                </CustomDialogTitle>
                <DialogContent style={{ margin: "10px" }}>
                    {
                        isImport ?
                            <ImportPopup showExportButton={false}
                                scorecardId={props.scorecardId}
                                uploadType={props.uploadType}
                                controlsEnabled={props.controlsEnabled}
                                showHelpfileDownload={props.showHelpfileDownload}
                                importBus={props.importBus}
                                showYearToAdd={props.showYearToAdd}
                                coreImportEnabled={props.coreImportEnabled}
                                typeId={props.typeId}
                            />
                            :
                            <Box mt={1}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <MobileDatePicker
                                        views={['year']}
                                        inputFormat="yyyy"
                                        label="Select Year to Export"
                                        value={yearToExport}
                                        onChange={(date) => {
                                            if (date != null) {
                                                setYearToExport(getDateWithoutTime(date));
                                            }
                                        }}
                                        renderInput={(params) => <TextField {...params} fullWidth variant='outlined' size='small' />}
                                    />
                                </LocalizationProvider>
                            </Box>
                    }

                </DialogContent>
                <DialogActions>
                    {!isImport && <MDButton variant="contained" color="primary" onClick={() => {
                        coreFileExport();
                        setOpenImportPopup(false);
                    }}>Submit</MDButton>}
                </DialogActions>
            </Dialog>
            <Grid container >
                <Grid item xs={12} lg={12}>
                    <MDBox mt={4}>
                        <MDAlert color='light'>
                            {isLoading ?
                                <Grid container justifyContent="center" alignItems="center" direction="column">
                                    <Typography variant="h6" mb={1}>Exporting file...</Typography>
                                    <CircularProgress />
                                </Grid> :
                                <MDTypography component='span' variant='body' fontSize='0.93rem' fontWeight='regular'>


                                    {props.disableImport === true ?
                                        null
                                        :
                                        <p style={{ marginBottom: "0.2rem" }}> Click<Button variant="text" onClick={() => {
                                            setIsImport(true);
                                            setOpenImportPopup(true);
                                        }} size="small" style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
                                            to Import data from a file</p>
                                    }

                                    {props.disableExport === true ?
                                        null
                                        :
                                        <p style={{ marginBottom: "0.2rem" }}> Click<Button variant="text" onClick={onExportData} size="small" style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
                                            to Export data to a file</p>
                                    }

                                </MDTypography>
                            }
                        </MDAlert>
                    </MDBox>
                </Grid>
            </Grid>
        </>
    );
}